import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../../components/page/PageHeader';
import { useAuth } from '../../../context/AuthContext';
import { usePlan } from '../../../context/PlanContext';
import { useWikiV2Client } from '../../../hooks/useWikiV2';
import WikiFrame from '../shared/WikiFrame';
import WikiIcon from '../shared/WikiIcon';
import { WikiLoading } from '../shared/WikiStates';
import { useWikiCategories, useWikiHome, useWikiPage, wikiKeys } from '../shared/useWikiQueries';
import { wikiUi } from '../shared/wikiUi';
import { wikiPaths } from '../shared/wikiRoutes';
import { ASK_COPY, ASK_SUGGESTIONS } from './askCopy';
import { askStore, SCOPE_ALL, useAskStore } from './askStore';
import AskTurn from './AskTurn';
import AskHandoff from './AskHandoff';
import './ask.css';

/**
 * Ask the Wiki (`/wiki/ask`, D-ask §1) — the one bounded full-height Wiki
 * route (RuntimeShell `isFullscreenPage`; `<WikiFrame bounded>` re-applies the
 * Dashboard padding + 1600px container). Three blocks in a flex column:
 *
 *   head      PageHeader (title, subtitle, "New conversation") + scope chips
 *   thread    the ONLY scrolling region (`flex-1 min-h-0 overflow-y-auto`)
 *   composer  fixed at the bottom as a normal flex child (never position:fixed)
 *
 * The conversation lives in `askStore` (module level) so it survives leaving
 * Ask and coming back; `thread_id` goes out with every follow-up. Entry points
 * hand over a question / scope with `wikiUi.setAskPrefill` (consumed here) or
 * `?category=` / `?page=` / `?q=`; `?handoff=` shows a hand-off snapshot.
 */

const selectState = (s) => s;

function scopeKey(scope) {
    if (scope?.pageId) return `p:${scope.pageId}`;
    if (scope?.categoryId) return `c:${scope.categoryId}`;
    return 'all';
}

export default function WikiAsk() {
    const client = useWikiV2Client();
    const { user } = useAuth();
    const { planUnknown, checkFeature } = usePlan();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const categories = useWikiCategories();
    const home = useWikiHome();
    const store = useAskStore(selectState);
    const threadRef = useRef(null);
    const inputRef = useRef(null);

    const meId = user?.user_id || user?.id || null;
    const ownerKey = user ? `${user.org_id || ''}:${meId || ''}` : 'anon';
    const search = location?.search || '';
    const params = useMemo(() => new URLSearchParams(search), [search]);
    const handoffId = params.get('handoff');
    // Category/page-scoped answers are the `wiki_category_assistants` plan
    // feature (off on free/starter; the API answers 403 wiki_feature_disabled).
    // Plan not known yet → allow; the server stays authoritative.
    const scopedAllowed = planUnknown || checkFeature('wiki_category_assistants');

    // Render nothing of another user's conversation, even for the one frame
    // before bindOwner runs.
    const bound = store.ownerKey === ownerKey;
    const rows = bound ? store.rows : [];
    const scope = bound ? store.scope : SCOPE_ALL;
    const extraScope = bound ? store.extraScope : null;
    const input = bound ? store.input : '';
    const sending = bound && store.sending;
    const hydrating = bound && store.hydrating;
    const pendingCount = rows.filter((row) => row.pending).length;
    const extraPage = useWikiPage(extraScope?.pageId || null);

    // A category's own "Category assistant" switch: `assistant_enabled: false`
    // on the category or its parent (or, for a page, on the page's
    // category/subcategory) turns scoped Ask off there — BE-ask refuses with
    // 403 wiki_category_assistant_off. True only when we KNOW it's off (the
    // category / page is in the cache); unknown → allowed.
    const isScopeBlocked = useCallback((target) => {
        const categoryOff = (id) => {
            const found = id ? categories.find(id) : null;
            if (!found) return false;
            return found.category.assistant_enabled === false || found.parent?.assistant_enabled === false;
        };
        if (!target) return false;
        if (target.pageId) {
            const page = queryClient.getQueryData(wikiKeys.page(target.pageId))
                || (extraPage.data?.page_id === target.pageId ? extraPage.data : null);
            if (!page) return false;
            return categoryOff(page.subcategory_id) || categoryOff(page.category_id);
        }
        return categoryOff(target.categoryId);
    }, [categories, queryClient, extraPage.data]);

    // Bind the thread to this user, then apply any hand-off — before paint, so
    // a Home chip lands straight on its pending row (no empty-state flash).
    useLayoutEffect(() => {
        askStore.bindOwner(ownerKey);
    }, [ownerKey]);

    useLayoutEffect(() => {
        const prefill = wikiUi.consumeAskPrefill();
        if (!prefill) return;
        const allowScope = scopedAllowed && !isScopeBlocked({ categoryId: prefill.categoryId, pageId: prefill.pageId });
        askStore.applyPrefill(client, prefill, { allowScope });
    }, [client, scopedAllowed, isScopeBlocked]);

    useLayoutEffect(() => {
        const question = params.get('q');
        if (question) askStore.setInput(question);
    }, [params]);

    useLayoutEffect(() => {
        const categoryId = params.get('category');
        const pageId = params.get('page');
        if (scopedAllowed && (categoryId || pageId)) askStore.presetScope({ categoryId, pageId });
    }, [params, scopedAllowed]);

    // A scope that turned out to be unusable once the plan / categories / page
    // loaded (hand-off or URL applied before we knew): back to the whole Wiki.
    useLayoutEffect(() => {
        if (!bound) return;
        if (!scopedAllowed) { askStore.clearScope(); return; }
        askStore.clearScope({ scope: isScopeBlocked(scope), extra: isScopeBlocked(extraScope) });
    }, [scopedAllowed, bound, scope, extraScope, isScopeBlocked]);

    // After a reload, restore this tab's conversation.
    useEffect(() => {
        askStore.hydrate(client);
    }, [client, ownerKey]);

    // Prototype auto-scroll: when a question is added or an answer lands, pin
    // the LAST question 16px from the top of the thread so the answer flows
    // beneath it.
    useEffect(() => {
        const el = threadRef.current;
        if (!el || rows.length === 0) return undefined;
        const timer = setTimeout(() => {
            const bubbles = el.querySelectorAll('[data-wk-user]');
            const last = bubbles[bubbles.length - 1];
            if (last) el.scrollTop = Math.max(0, last.offsetTop - 16);
        }, 40);
        return () => clearTimeout(timer);
    }, [rows.length, pendingCount]);

    // Grow the composer with its content (Shift+Enter lines), up to its max.
    useLayoutEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight}px`;
        else el.style.removeProperty('height');
    }, [input]);

    const chips = useMemo(() => {
        const list = [{ key: 'all', label: ASK_COPY.allScope, scope: SCOPE_ALL }];
        if (!scopedAllowed) return list;
        const topIds = new Set(categories.tree.map((cat) => cat.id));
        if (extraScope && !isScopeBlocked(extraScope)
            && (extraScope.pageId || !topIds.has(extraScope.categoryId))) {
            let label = 'This page';
            let icon = 'file-text';
            if (extraScope.pageId) {
                label = extraPage.data?.title || label;
            } else {
                icon = 'folder';
                const found = categories.find(extraScope.categoryId);
                label = found
                    ? (found.parent ? `${found.parent.name} › ${found.category.name}` : found.category.name)
                    : 'This category';
            }
            list.push({ key: scopeKey(extraScope), label, icon, scope: extraScope });
        }
        categories.tree.forEach((cat) => {
            // Category assistant switched off → no chip (its subcategories inherit).
            if (cat.assistant_enabled === false) return;
            list.push({ key: `c:${cat.id}`, label: cat.name, scope: { categoryId: cat.id, pageId: null } });
        });
        return list;
    }, [categories, extraScope, extraPage.data, scopedAllowed, isScopeBlocked]);

    // Starter questions = the Home hero's (`wkSuggest`, one list in the
    // prototype): the org's configured/derived list from GET /wiki/home, else
    // the prototype four once that request has settled.
    const serverSuggestions = Array.isArray(home.data?.suggestions)
        ? home.data.suggestions.filter((q) => typeof q === 'string' && q.trim()).slice(0, 4)
        : [];
    let suggestions = [];
    if (serverSuggestions.length > 0) suggestions = serverSuggestions;
    else if (home.isSuccess || home.isError) suggestions = ASK_SUGGESTIONS;

    const selectedKey = scopeKey(scope);
    const hasThread = rows.length > 0;
    const showEmpty = !hasThread && !hydrating && !handoffId;

    const send = () => {
        if (sending) return;
        askStore.ask(client, input);
    };

    const onKeyDown = (event) => {
        if (event.key !== 'Enter' || event.shiftKey) return;
        if (event.nativeEvent?.isComposing) return;
        event.preventDefault();
        send();
    };

    const newConversation = (
        <button
            type="button"
            className="wk-btn wk-btn--ghost wk-btn--sm wk-ask-new"
            onClick={() => askStore.newConversation()}
            data-testid="wiki-ask-new"
        >
            <WikiIcon name="refresh-cw" size={14} />
            {ASK_COPY.newConversation}
        </button>
    );

    return (
        <WikiFrame bounded className="wk-ask" testId="wiki-ask">
            <div className="wk-ask-head">
                <div className="wk-ask-head-row">
                    <span className="wk-ask-tile wk-ask-tile--brand" aria-hidden="true">
                        <WikiIcon name="sparkles" size={16} />
                    </span>
                    <div className="wk-ask-head-main">
                        <PageHeader
                            title={ASK_COPY.title}
                            subtitle={ASK_COPY.subtitle}
                            right={hasThread ? newConversation : null}
                        />
                    </div>
                </div>
                <div className="wk-ask-scopes" role="group" aria-label="Answer from" data-testid="wiki-ask-scopes">
                    {chips.map((chip) => (
                        <button
                            key={chip.key}
                            type="button"
                            className={`wk-ask-scope ${chip.key === selectedKey ? 'is-selected' : ''}`.trim()}
                            aria-pressed={chip.key === selectedKey}
                            onClick={() => askStore.setScope(chip.scope)}
                            data-scope={chip.key}
                        >
                            {chip.icon && <WikiIcon name={chip.icon} size={12} />}
                            {chip.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="wk-ask-thread" ref={threadRef} data-wk-thread="1" data-testid="wiki-ask-thread">
                <div className="wk-ask-col">
                    {handoffId && (
                        <AskHandoff handoffId={handoffId} meId={meId} onClose={() => navigate(wikiPaths.ask())} />
                    )}
                    {hydrating && !hasThread && <WikiLoading label="Loading your conversation…" />}
                    {showEmpty && (
                        <div className="wk-ask-empty" data-testid="wiki-ask-empty">
                            <h2 className="wk-ask-empty-title">{ASK_COPY.emptyTitle}</h2>
                            <p className="wk-ask-empty-detail">{ASK_COPY.emptyDetail}</p>
                            <div className="wk-ask-suggest">
                                {suggestions.map((text) => (
                                    <button
                                        key={text}
                                        type="button"
                                        className="wk-ask-suggestion"
                                        disabled={sending}
                                        onClick={() => askStore.ask(client, text)}
                                    >
                                        <WikiIcon name="message-square" size={14} className="wk-ask-suggestion-icon" />
                                        {text}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {rows.map((row) => <AskTurn key={row.id} row={row} meId={meId} />)}
                </div>
            </div>

            <form
                className="wk-ask-composer"
                onSubmit={(event) => { event.preventDefault(); send(); }}
                data-testid="wiki-ask-composer"
            >
                <div className="wk-ask-box">
                    <textarea
                        ref={inputRef}
                        className="wk-ask-input"
                        rows={1}
                        value={input}
                        maxLength={4000}
                        placeholder={ASK_COPY.placeholder}
                        aria-label={ASK_COPY.title}
                        aria-busy={sending || undefined}
                        onChange={(event) => askStore.setInput(event.target.value)}
                        onKeyDown={onKeyDown}
                        data-testid="wiki-ask-input"
                    />
                    <button
                        type="submit"
                        className="wk-btn wk-btn--teal wk-ask-send"
                        disabled={sending}
                        data-testid="wiki-ask-send"
                    >
                        <WikiIcon name="send-horizontal" size={14} />
                        {ASK_COPY.send}
                    </button>
                </div>
                <p className="wk-ask-hint">{ASK_COPY.hint}</p>
            </form>
        </WikiFrame>
    );
}
